import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  ListGroup,
  Row,
  Spinner,
  Table,
} from "@themesberg/react-bootstrap";
import Swal from "sweetalert2";
import api from "../../utils/api";

export default function ReportDetail() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [actionHistory, setActionHistory] = useState([]);
  const [statusLogs, setStatusLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const entityBlock = useMemo(() => {
    if (!report) return null;
    if (report.entity_type === "user") {
      return { label: "Reported User", value: report.reportedUser || null };
    }
    return { label: "Reported Group", value: report.reportedGroup || null };
  }, [report]);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/reports/${id}`);
      const payload = res.data?.data || res.data?.data?.data;
      setReport(payload || null);
      setActionHistory(payload?.actionHistory || []);
      setStatusLogs(payload?.statusLogs || []);
    } catch (e) {
      setError(
        e?.response?.data?.message || e.message || "Failed to load report",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const statusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge bg="warning">Pending</Badge>;
      case "reviewed":
        return <Badge bg="primary">Reviewed</Badge>;
      case "action_taken":
        return <Badge bg="success">Action taken</Badge>;
      case "rejected":
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleUpdateStatus = async (nextStatus) => {
    const confirm = await Swal.fire({
      title: "Update status?",
      text: `Change status to "${nextStatus}"`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      heightAuto: false,
    });

    if (!confirm.isConfirmed) return;
    try {
      await api.patch(`/reports/${id}/status`, { status: nextStatus });
      await refresh();
      await Swal.fire({
        title: "Success",
        text: "Status updated",
        icon: "success",
        heightAuto: false,
      });
    } catch (e) {
      await Swal.fire({
        title: "Error",
        text: e?.response?.data?.message || e.message || "Failed",
        icon: "error",
        heightAuto: false,
      });
    }
  };

  const handleTakeAction = async (action_type) => {
    const result = await Swal.fire({
      title: "Action notes",
      input: "textarea",
      inputLabel: "Please describe the admin action",
      inputPlaceholder: "e.g. Blocked user for abusive behavior...",
      inputAttributes: {
        autocapitalize: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      heightAuto: false,
      preConfirm: (notes) => {
        if (!notes || notes.trim().length < 2) {
          Swal.showValidationMessage("Please enter at least 2 characters");
          return false;
        }
        return notes;
      },
    });

    if (!result.isConfirmed) return;

    const notes = result.value || ""; // ✅ now this will be the actual text

    try {
      await api.post(`/reports/${id}/action`, {
        action_type,
        action_notes: notes,
      });
      await refresh();
      await Swal.fire({
        title: "Success",
        text: "Action submitted",
        icon: "success",
        heightAuto: false,
      });
    } catch (e) {
      await Swal.fire({
        title: "Error",
        text: e?.response?.data?.message || e.message || "Failed",
        icon: "error",
        heightAuto: false,
      });
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 250 }}
      >
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!report) {
    return <Alert variant="warning">Report not found</Alert>;
  }

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : "-");

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div>
          <h2 className="h4 mb-0">Report #{report.id}</h2>
          <p className="mb-0 text-muted">
            Manage report status and admin actions
          </p>
        </div>
        <div className="d-flex gap-2">
          {statusBadge(report.status)}
          <Badge bg="secondary" className="ms-2">
            Priority: {report.priority}
          </Badge>
        </div>
      </div>

      <Row>
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Report Details</h5>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item className="px-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">Reporter</span>
                    <span>
                      {report.reportedBy?.name || "-"} (
                      {report.reportedBy?.email || "-"})
                    </span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item className="px-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{entityBlock?.label}</span>
                    <span>{entityBlock?.value?.name || "-"}</span>
                  </div>
                  {entityBlock?.value?.email ? (
                    <div className="small text-muted">
                      {entityBlock.value.email}
                    </div>
                  ) : null}
                </ListGroup.Item>
                <ListGroup.Item className="px-0">
                  <div className="fw-bold">Reason</div>
                  <div>{report.reason}</div>
                </ListGroup.Item>
                <ListGroup.Item className="px-0">
                  <div className="fw-bold">Notes</div>
                  <div>{report.notes || "-"}</div>
                </ListGroup.Item>
                <ListGroup.Item className="px-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">Created</span>
                    <span>{formatDate(report.created_at)}</span>
                  </div>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8} className="mb-4">
          <Card className="border-0 shadow">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Admin Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                  variant="outline-primary"
                  onClick={() => handleUpdateStatus("reviewed")}
                >
                  Mark reviewed
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={() => handleUpdateStatus("rejected")}
                >
                  Reject
                </Button>

                <div style={{ width: "100%" }} />

                <Button
                  variant="outline-warning"
                  onClick={() => handleTakeAction("warning")}
                >
                  Warning
                </Button>
                {report.entity_type === "user" ? (
                  <Button
                    variant="outline-dark"
                    onClick={() => handleTakeAction("block_user")}
                  >
                    Block user
                  </Button>
                ) : null}
                {report.entity_type === "group" ? (
                  <Button
                    variant="outline-dark"
                    onClick={() => handleTakeAction("delete_group")}
                  >
                    Delete group
                  </Button>
                ) : null}
                <Button
                  variant="outline-secondary"
                  onClick={() => handleTakeAction("no_action")}
                >
                  No action
                </Button>
              </div>

              <hr />

              <h6 className="mb-2">Status Logs</h6>
              {statusLogs?.length ? (
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Old</th>
                      <th>New</th>
                      <th>Changed by</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusLogs.map((l) => (
                      <tr key={l.id}>
                        <td>{l.old_status}</td>
                        <td>{l.new_status}</td>
                        <td>{l.changedBy?.name || "-"}</td>
                        <td>{formatDate(l.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-muted">No status logs yet</div>
              )}

              <hr />

              <h6 className="mb-2">Action History</h6>
              {actionHistory?.length ? (
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Admin</th>
                      <th>Notes</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actionHistory.map((a) => (
                      <tr key={a.id}>
                        <td>{a.action_type}</td>
                        <td>{a.admin?.name || "-"}</td>
                        <td>{a.action_notes}</td>
                        <td>{formatDate(a.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-muted">No admin actions yet</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
